import { useState, useEffect } from 'react';
import { Users, Mail, Briefcase } from 'lucide-react';

interface TargetFilters {
  status?: string | null;
  source?: string | null;
  specific_emails?: string[];
}

interface TargetAudienceSelectorProps {
  targetTable: string;
  targetFilters: TargetFilters;
  onTargetTableChange: (table: string) => void;
  onTargetFiltersChange: (filters: TargetFilters) => void;
  onPreviewRecipients: () => void;
  adminToken: string;
}

export default function TargetAudienceSelector({
  targetTable,
  targetFilters,
  onTargetTableChange,
  onTargetFiltersChange,
  onPreviewRecipients,
  adminToken,
}: TargetAudienceSelectorProps) {
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [sourceOptions, setSourceOptions] = useState<string[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (adminToken) {
      loadFilterOptions();
    }
  }, [targetTable, adminToken]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRecipientCount();
    }, 500);

    return () => clearTimeout(timer);
  }, [targetTable, targetFilters]);

  const loadFilterOptions = async () => {
    setLoadingOptions(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/campaigns-options?table=${targetTable}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'X-Admin-Token': adminToken,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const { options } = await response.json();

        if (targetTable === 'game_dev_applications') {
          setStatusOptions(options);
        } else if (targetTable === 'email_list') {
          setSourceOptions(options);
        }
      } else {
        console.error('Failed to load filter options:', await response.text());
      }
    } catch (error) {
      console.error('Failed to load filter options:', error);
    } finally {
      setLoadingOptions(false);
    }
  };

  const fetchRecipientCount = async () => {
    if (!adminToken) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/campaigns-preview`,
        {
          method: 'POST',
          headers: {
            'X-Admin-Token': adminToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            target_table: targetTable,
            target_filters: targetFilters,
            limit: 100,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setRecipientCount(data.count);
      }
    } catch (error) {
      console.error('Failed to fetch recipient count:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (status: string) => {
    onTargetFiltersChange({
      ...targetFilters,
      status: status === 'all' ? null : status,
    });
  };

  const handleSourceChange = (source: string) => {
    onTargetFiltersChange({
      ...targetFilters,
      source: source === 'all' ? null : source,
    });
  };

  const formatSourceLabel = (source: string): string => {
    return source
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-bold text-gray-300 mb-3">
          Target Audience
        </label>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => onTargetTableChange('game_dev_applications')}
            className={`p-6 rounded-lg border-2 transition-all ${
              targetTable === 'game_dev_applications'
                ? 'border-yellow-400 bg-yellow-400/10'
                : 'border-yellow-400/30 hover:border-yellow-400/50 bg-black/50'
            }`}
          >
            <Briefcase className="w-8 h-8 mb-3 mx-auto text-yellow-400" />
            <h3 className="font-semibold text-white mb-1">
              Game Developer Applicants
            </h3>
            <p className="text-sm text-gray-400">
              Target applicants based on their application status
            </p>
          </button>

          <button
            type="button"
            onClick={() => onTargetTableChange('email_list')}
            className={`p-6 rounded-lg border-2 transition-all ${
              targetTable === 'email_list'
                ? 'border-yellow-400 bg-yellow-400/10'
                : 'border-yellow-400/30 hover:border-yellow-400/50 bg-black/50'
            }`}
          >
            <Mail className="w-8 h-8 mb-3 mx-auto text-yellow-400" />
            <h3 className="font-semibold text-white mb-1">
              Newsletter Subscribers
            </h3>
            <p className="text-sm text-gray-400">
              Target newsletter subscribers by signup source
            </p>
          </button>
        </div>
      </div>

      {targetTable === 'game_dev_applications' && (
        <div>
          <label className="block text-sm font-bold text-gray-300 mb-2">
            Application Status
          </label>
          <select
            value={targetFilters.status || 'all'}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={loadingOptions}
            className="w-full px-4 py-2 bg-black border border-yellow-400/30 rounded-lg text-white focus:border-yellow-400 focus:outline-none disabled:opacity-50"
          >
            <option value="all">All Statuses</option>
            {loadingOptions ? (
              <option disabled>Loading...</option>
            ) : statusOptions.length > 0 ? (
              statusOptions.map(status => (
                <option key={status} value={status}>{status}</option>
              ))
            ) : (
              <option disabled>No statuses found</option>
            )}
          </select>
        </div>
      )}

      {targetTable === 'email_list' && (
        <div>
          <label className="block text-sm font-bold text-gray-300 mb-2">
            Signup Source
          </label>
          <select
            value={targetFilters.source || 'all'}
            onChange={(e) => handleSourceChange(e.target.value)}
            disabled={loadingOptions}
            className="w-full px-4 py-2 bg-black border border-yellow-400/30 rounded-lg text-white focus:border-yellow-400 focus:outline-none disabled:opacity-50"
          >
            <option value="all">All Sources</option>
            {loadingOptions ? (
              <option disabled>Loading...</option>
            ) : sourceOptions.length > 0 ? (
              sourceOptions.map(source => (
                <option key={source} value={source}>{formatSourceLabel(source)}</option>
              ))
            ) : (
              <option disabled>No sources found</option>
            )}
          </select>
        </div>
      )}

      <div className="bg-black/50 border border-yellow-400/30 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-yellow-400" />
          <div>
            <p className="text-sm font-medium text-white">
              {loading ? (
                'Calculating...'
              ) : recipientCount !== null ? (
                `${recipientCount} recipient${recipientCount !== 1 ? 's' : ''} will receive this email`
              ) : (
                'Select filters to see recipient count'
              )}
            </p>
            {recipientCount !== null && !loading && (
              <p className="text-xs text-gray-400">
                {targetTable === 'email_list'
                  ? 'Based on current selection and marketing consent'
                  : 'Based on current selection'
                }
              </p>
            )}
          </div>
        </div>
        {recipientCount !== null && recipientCount > 0 && (
          <button
            type="button"
            onClick={onPreviewRecipients}
            className="px-4 py-2 text-sm font-medium text-yellow-400 hover:text-yellow-300 hover:bg-yellow-400/10 rounded-lg transition-colors"
          >
            Preview Recipients
          </button>
        )}
      </div>

      {recipientCount === 0 && !loading && (
        <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
          <p className="text-sm text-yellow-200">
            {targetTable === 'email_list'
              ? 'No recipients match your current selection. Try adjusting your filters or check that users have marketing consent enabled.'
              : 'No recipients match your current selection. Try adjusting your filters.'
            }
          </p>
        </div>
      )}
    </div>
  );
}
